import path from 'node:path'
import * as p from '@clack/prompts'
import type { AddFieldCommandOptions } from '@core-engine/commands/runtime.js'
import {
  type AddFieldOption,
  applySlotToField,
  buildLeafField,
  type GeneratedSchemaField,
  getSchemaDisplayName,
  insertFieldIntoTarget,
  isRequiredPersistedSchemaField,
  type LoadedAuthoredGeneratedSchema,
  loadAuthoredGeneratedSchema,
  NON_INTERACTIVE_CONTAINER_TYPES,
  parseAddFieldOptions,
  type RelationshipTargetKind,
  readRelationshipTargetKind,
  resolveDefaultPlacementTarget,
  validateAuthoredGeneratedSchema,
  writeAuthoredGeneratedSchema
} from '@core-engine/schema/add-field.js'
import { listSchemaNames } from '@core-engine/schema/schema-reader.js'
import { loadManifest, snapshotRootExists } from '@core-engine/snapshots/store.js'
import { stringifyProjectJson } from '@core-engine/utils/json-format.js'
import { resolveProjectPaths } from '@next-adapter/config/paths.js'
import { resolveConfig } from '@next-adapter/config.js'
import { getSchemaOwner } from '@preset-engine/ownership.js'
import type { SchemaOwner } from '@preset-engine/types.js'
import { runGenerateCommand } from './generate.js'
import {
  createInteractiveInsertion as createInteractiveFieldInsertion,
  formatKind,
  isInteractiveSession,
  type PreparedFieldInsertion,
  promptConfirm as promptSchemaConfirm,
  promptSelectValue as promptSchemaSelectValue,
  type SchemaPromptContext
} from './schema-prompts.js'

const ADD_FIELD_PROMPT_CONTEXT: SchemaPromptContext = {
  cancelMessage: 'Add field cancelled.'
}

function hasNonInteractiveFieldOptions(options: AddFieldCommandOptions): boolean {
  return Boolean(
    options.type ||
      options.field ||
      options.label ||
      options.slot ||
      options.step ||
      options.before ||
      options.after ||
      options.required ||
      options.multiple ||
      options.creatable ||
      options.relationship ||
      (options.option && options.option.length > 0)
  )
}

function fail(message: string): never {
  p.log.error(message)
  process.exit(1)
}

function cancel(message = 'Add field cancelled.'): never {
  p.cancel(message)
  process.exit(0)
}

function schemaNameFromLoaded(loaded: LoadedAuthoredGeneratedSchema): string {
  const schemaName = loaded.schema.name
  return typeof schemaName === 'string' && schemaName.trim() ? schemaName : loaded.name
}

function resolveSchemaOwner(cwd: string, schemaName: string): SchemaOwner {
  return getSchemaOwner(cwd, schemaName) ?? (schemaName === 'settings' ? 'core' : 'user')
}

function presetIdFromOwner(owner: SchemaOwner): string | null {
  return owner.startsWith('preset:') ? owner.replace(/^preset:/, '') : null
}

async function promptConfirm(message: string, initialValue = true): Promise<boolean> {
  return promptSchemaConfirm(ADD_FIELD_PROMPT_CONTEXT, message, initialValue)
}

async function promptSelectValue<T extends string>(
  message: string,
  options: Array<{ value: T; label: string }>,
  initialValue?: T
): Promise<T> {
  return promptSchemaSelectValue(ADD_FIELD_PROMPT_CONTEXT, message, options, initialValue)
}

function normalizeOptionList(options: AddFieldCommandOptions): AddFieldOption[] {
  try {
    return parseAddFieldOptions(options.option)
  } catch (error) {
    fail(error instanceof Error ? error.message : String(error))
  }
}

function validateRelationshipTarget(
  schemasDir: string,
  targetSchema: string | undefined,
  fieldType: string | undefined
): void {
  if (!targetSchema || fieldType !== 'relationship') {
    return
  }

  let targetKind: RelationshipTargetKind
  try {
    targetKind = readRelationshipTargetKind(schemasDir, targetSchema)
  } catch (error) {
    fail(`Relationship target error: ${error instanceof Error ? error.message : String(error)}`)
  }

  if (targetKind === 'form') {
    fail('Relationship fields can target Entity and Single schemas only, not form schemas.')
  }
}

function createNonInteractiveInsertion(
  loaded: LoadedAuthoredGeneratedSchema,
  schemasDir: string,
  options: AddFieldCommandOptions
): PreparedFieldInsertion {
  if (!options.type || !options.field || !options.label) {
    fail('Non-interactive add-field requires --type, --field, and --label.')
  }

  if (NON_INTERACTIVE_CONTAINER_TYPES.has(options.type)) {
    fail(`Non-interactive add-field does not support recursive "${options.type}" fields.`)
  }

  validateRelationshipTarget(schemasDir, options.relationship, options.type)

  const target = resolveDefaultPlacementTarget(loaded, {
    slot: options.slot,
    step: options.step
  })
  const field = applySlotToField(
    loaded,
    target,
    buildLeafField({
      kind: loaded.kind,
      type: options.type,
      name: options.field,
      label: options.label,
      required: options.required,
      multiple: options.multiple,
      creatable: options.creatable,
      options: normalizeOptionList(options),
      relationship: options.relationship
    }),
    options.slot
  )

  return {
    field,
    target,
    order: {
      before: options.before,
      after: options.after
    },
    summary: []
  }
}

async function createInteractiveInsertion(
  loaded: LoadedAuthoredGeneratedSchema,
  schemasDir: string
): Promise<PreparedFieldInsertion> {
  return createInteractiveFieldInsertion(ADD_FIELD_PROMPT_CONTEXT, loaded, schemasDir)
}

function printPreview(
  loaded: LoadedAuthoredGeneratedSchema,
  owner: SchemaOwner,
  firstTimeGeneration: boolean,
  insertion: PreparedFieldInsertion,
  options: AddFieldCommandOptions
): void {
  const presetId = presetIdFromOwner(owner)
  const lines = [
    `schema: ${loaded.name} (${formatKind(loaded.kind)})`,
    `display: ${getSchemaDisplayName(loaded)}`,
    `owner: ${owner}`,
    `target: ${insertion.target.label}`,
    `order: ${insertion.order.before ? `before ${insertion.order.before}` : insertion.order.after ? `after ${insertion.order.after}` : 'end'}`,
    `migration: ${options.skipMigration ? 'skip db:push' : 'run post-generate default'}`,
    `merge conflicts: ${options.acceptGenerated || options.yes ? 'accept generated output' : 'write conflict markers'}`
  ]

  if (presetId) {
    lines.push(
      `warning: preset-owned schema; uninstalling "${presetId}" can remove this schema and generated output`
    )
  }
  if (firstTimeGeneration) {
    lines.push('warning: no per-schema snapshot manifest; generation will start from fresh output')
  }
  if (insertion.summary.length > 0) {
    lines.push(`schema integration: ${insertion.summary.join(', ')}`)
  } else {
    lines.push('schema integration: none')
  }

  p.note(
    `${lines.join('\n')}\n\n${stringifyProjectJson(insertion.field).trim()}`,
    'Add field preview'
  )
}

async function confirmWarningCases(
  owner: SchemaOwner,
  firstTimeGeneration: boolean,
  requiredPersistedField: boolean,
  nonInteractive: boolean,
  yes: boolean | undefined,
  extraWarnings: string[] = []
): Promise<void> {
  const warnings: string[] = [...extraWarnings]
  const presetId = presetIdFromOwner(owner)
  if (presetId) {
    warnings.push(
      `Schema is preset-owned by "${presetId}". Removing that preset can remove the customized schema JSON, generated files, snapshots, and ownership entry.`
    )
  }
  if (firstTimeGeneration) {
    warnings.push('Schema JSON exists but no per-schema snapshot manifest was found.')
  }
  if (requiredPersistedField) {
    warnings.push(
      'Required persisted Entity/Single fields may need manual handling for existing rows.'
    )
  }

  if (warnings.length === 0) {
    return
  }

  if (nonInteractive) {
    if (!yes) {
      fail(`${warnings.join('\n')}\nRe-run with --yes to confirm these warning cases.`)
    }
    return
  }

  p.note(warnings.join('\n'), 'Warnings')
  const confirmed = await promptConfirm('Continue with these warnings?', true)
  if (!confirmed) {
    cancel()
  }
}

function collectRelationshipTargetWarnings(
  cwd: string,
  owner: SchemaOwner,
  field: GeneratedSchemaField
): string[] {
  if (owner !== 'user') {
    return []
  }

  const targetNames = new Set<string>()
  collectRelationshipTargets(field, targetNames)

  return Array.from(targetNames)
    .map((targetName) => {
      const targetOwner = getSchemaOwner(cwd, targetName)
      const presetId = targetOwner ? presetIdFromOwner(targetOwner) : null
      return presetId
        ? `This user-owned schema references preset-owned schema "${targetName}". Removing preset "${presetId}" may affect that relationship.`
        : null
    })
    .filter((warning): warning is string => Boolean(warning))
}

function collectRelationshipTargets(field: GeneratedSchemaField, targets: Set<string>): void {
  if ('relationship' in field && typeof field.relationship === 'string') {
    targets.add(field.relationship)
  }

  if (field.fields) {
    for (const child of field.fields) {
      collectRelationshipTargets(child, targets)
    }
  }

  if ('tabs' in field && field.tabs) {
    for (const tab of field.tabs) {
      for (const child of tab.fields ?? []) {
        collectRelationshipTargets(child, targets)
      }
      for (const child of tab.slot?.main?.fields ?? []) {
        collectRelationshipTargets(child, targets)
      }
      for (const child of tab.slot?.sidebar?.fields ?? []) {
        collectRelationshipTargets(child, targets)
      }
    }
  }
}

async function resolveSchemaNameInteractively(
  schemasDir: string,
  schemaName: string | undefined
): Promise<string> {
  if (schemaName) {
    return schemaName
  }

  if (!isInteractiveSession()) {
    fail('Schema name is required in non-interactive sessions.')
  }

  const schemaNames = listSchemaNames(schemasDir)
  if (schemaNames.length === 0) {
    fail(`No schemas found in ${schemasDir}.`)
  }

  return promptSelectValue(
    'Generated schema',
    schemaNames.map((name) => ({ value: name, label: name }))
  )
}

export async function runAddFieldCommand(
  schemaName: string | undefined,
  options: AddFieldCommandOptions
): Promise<void> {
  const cwd = options.cwd ? path.resolve(options.cwd) : process.cwd()
  const nonInteractive = hasNonInteractiveFieldOptions(options)

  if (!snapshotRootExists(cwd)) {
    fail(
      'Project state is unsupported or incomplete. Re-run `betterstart init --force` to create the current snapshot-backed layout.'
    )
  }

  const config = await resolveConfig(cwd)
  const paths = resolveProjectPaths(config)
  const schemasDir = path.join(cwd, ...paths.schemasDir.split('/'))
  const selectedSchemaName = await resolveSchemaNameInteractively(schemasDir, schemaName)

  let loaded: LoadedAuthoredGeneratedSchema
  try {
    loaded = loadAuthoredGeneratedSchema(schemasDir, selectedSchemaName)
  } catch (error) {
    fail(error instanceof Error ? error.message : String(error))
  }

  if (!nonInteractive && !isInteractiveSession()) {
    fail('Interactive add-field requires a TTY. Provide --type, --field, and --label instead.')
  }

  const owner = resolveSchemaOwner(cwd, schemaNameFromLoaded(loaded))
  const firstTimeGeneration = !loadManifest(cwd, schemaNameFromLoaded(loaded))
  let insertion: PreparedFieldInsertion
  try {
    insertion = nonInteractive
      ? createNonInteractiveInsertion(loaded, schemasDir, options)
      : await createInteractiveInsertion(loaded, schemasDir)
  } catch (error) {
    fail(error instanceof Error ? error.message : String(error))
  }

  const requiredPersistedField = isRequiredPersistedSchemaField(
    loaded,
    insertion.field,
    insertion.target
  )

  await confirmWarningCases(
    owner,
    firstTimeGeneration,
    requiredPersistedField,
    nonInteractive,
    options.yes,
    collectRelationshipTargetWarnings(cwd, owner, insertion.field)
  )

  try {
    insertFieldIntoTarget(insertion.target, insertion.field, insertion.order)
  } catch (error) {
    fail(error instanceof Error ? error.message : String(error))
  }

  const validationErrors = validateAuthoredGeneratedSchema(loaded)
  if (validationErrors.length > 0) {
    fail(
      `Schema validation failed after insertion:\n${validationErrors.map((error) => `  - ${error}`).join('\n')}`
    )
  }

  printPreview(loaded, owner, firstTimeGeneration, insertion, options)

  if (!nonInteractive && !options.yes) {
    const confirmed = await promptConfirm('Write schema JSON and regenerate this schema?', true)
    if (!confirmed) {
      cancel()
    }
  }

  writeAuthoredGeneratedSchema(loaded)
  p.log.success(`Updated ${path.relative(cwd, loaded.filePath)}`)

  try {
    await runGenerateCommand(selectedSchemaName, {
      force: false,
      acceptGenerated: Boolean(options.acceptGenerated || options.yes),
      all: false,
      interactive: true,
      skipMigration: Boolean(options.skipMigration),
      cwd
    })
  } catch (error) {
    p.log.error(error instanceof Error ? error.message : String(error))
    p.log.info(
      `Schema JSON was kept. Re-run \`betterstart generate ${selectedSchemaName}${options.skipMigration ? ' --skip-migration' : ''}\` after resolving the issue.`
    )
    process.exit(1)
  }
}
